import type { ReactNode } from 'react';
import {
    DocumentDuplicateIcon,
    EyeIcon,
    EyeSlashIcon,
    PencilSquareIcon,
    TrashIcon,
    UsersIcon
} from '@heroicons/react/24/outline';
import { SolidStatusPill, Tooltip, type DataTableColumn } from '@odo/shared-ui';
import { RowMenu, type RowMenuItem } from '../billing/RowMenu';
import type { EventListRow } from '../../services/events';
import { formatShortDate } from '../../utils/dates';
import { eventKindBadgeClass, eventKindLabel, hasSignups } from './eventKind';
import { EVENT_STATUS_LABEL, EVENT_STATUS_TONE, eventStatus } from './eventStatus';

export type EventsSortBy = 'title' | 'startsAt' | 'showFrom' | 'showUntil';

// Same convention as practiceColumns.tsx / patientColumns.tsx: no px-6, and
// first/last cells align with the page gutter rather than the cell grid.
const EVENTS_TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';
const EVENTS_TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

interface MenuCallbacks {
    onEdit: (event: EventListRow) => void;
    onDuplicate: (event: EventListRow) => void;
    onToggleEnabled: (event: EventListRow) => void;
    onViewSignups: (event: EventListRow) => void;
    onDelete: (event: EventListRow) => void;
    isToggling: boolean;
    canManage: boolean;
    /** `events.delete` - a separate IAM permission from `events.update`. */
    canDelete: boolean;
    /** `events.get` - PII (attendee email/name), gated separately from reading the event itself. */
    canViewSignups: boolean;
}

// Plain functions, not components - column-preset render output only. The
// slug isn't shown: it's derived from the title, an internal key not a field.
function eventCell(event: EventListRow): ReactNode {
    return (
        <div className="flex flex-col min-w-0">
            <Tooltip content={event.title}>
                <span className="font-semibold text-gray-900 truncate min-w-0">{event.title}</span>
            </Tooltip>
            {event.topic && (
                <Tooltip content={event.topic}>
                    <span className="block text-[12px] text-gray-500 truncate">{event.topic}</span>
                </Tooltip>
            )}
        </div>
    );
}

// Without `events.update` the menu offers reads only - the API already
// refuses the write, this just keeps the UI from offering one that'd 403.
function menuItemsFor(
    event: EventListRow,
    {
        onEdit,
        onDuplicate,
        onToggleEnabled,
        onViewSignups,
        onDelete,
        isToggling,
        canManage,
        canDelete,
        canViewSignups
    }: MenuCallbacks
): RowMenuItem[] {
    // Independent of `canManage` - appended below regardless of which branch
    // runs, so gating tracks each action's own IAM permission, not `events.update`.
    const viewSignupsItem: RowMenuItem[] =
        canViewSignups && hasSignups(event)
            ? [
                  {
                      label: 'View sign-ups',
                      icon: <UsersIcon className="h-4 w-4 text-gray-400" />,
                      onClick: () => onViewSignups(event)
                  }
              ]
            : [];

    const deleteItem: RowMenuItem[] = canDelete
        ? [
              {
                  label: 'Delete event',
                  icon: <TrashIcon className="h-4 w-4 text-red-400" />,
                  tone: 'danger',
                  onClick: () => onDelete(event)
              }
          ]
        : [];

    if (!canManage) {
        return [
            {
                label: 'View event',
                icon: <PencilSquareIcon className="h-4 w-4 text-gray-400" />,
                onClick: () => onEdit(event)
            },
            ...viewSignupsItem,
            ...deleteItem
        ];
    }

    return [
        {
            label: 'Edit event',
            icon: <PencilSquareIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onEdit(event)
        },
        {
            label: 'Duplicate event',
            icon: <DocumentDuplicateIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onDuplicate(event)
        },
        ...viewSignupsItem,
        {
            label: event.enabled ? 'Disable event' : 'Enable event',
            icon: event.enabled ? (
                <EyeSlashIcon className="h-4 w-4 text-gray-400" />
            ) : (
                <EyeIcon className="h-4 w-4 text-gray-400" />
            ),
            disabled: isToggling,
            disabledReason: 'Saving…',
            onClick: () => onToggleEnabled(event)
        },
        ...deleteItem
    ];
}

export interface BuildEventColumnsOptions {
    onEdit: (event: EventListRow) => void;
    onDuplicate: (event: EventListRow) => void;
    onToggleEnabled: (event: EventListRow) => void;
    onViewSignups: (event: EventListRow) => void;
    onDelete: (event: EventListRow) => void;
    /** id of the row whose toggle mutation is in flight - disables its item only. */
    togglingId: string | null;
    /** `events.update`. False hides edit/duplicate/toggle from the row menu. */
    canManage: boolean;
    /** `events.delete`, independent of `canManage` - can show even when `canManage` is false. */
    canDelete: boolean;
    /** `events.get`, independent of `canManage` - gates the PII "View sign-ups" item. */
    canViewSignups: boolean;
}

export function buildEventColumns({
    onEdit,
    onDuplicate,
    onToggleEnabled,
    onViewSignups,
    onDelete,
    togglingId,
    canManage,
    canDelete,
    canViewSignups
}: BuildEventColumnsOptions): DataTableColumn<EventListRow, EventsSortBy>[] {
    return [
        {
            key: 'event',
            label: 'Event',
            width: '20%',
            sortable: true,
            sortKey: 'title',
            cellClassName: EVENTS_TD_PADDING,
            headerClassName: EVENTS_TH_PADDING,
            render: eventCell
        },
        {
            // A classification, not a state - a light badge rather than the
            // saturated SolidStatusPill the Status column uses.
            key: 'kind',
            label: 'Kind',
            width: '10%',
            cellClassName: EVENTS_TD_PADDING,
            headerClassName: `${EVENTS_TH_PADDING} whitespace-nowrap`,
            render: (event) => (
                <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${eventKindBadgeClass(event.kind)}`}
                >
                    {eventKindLabel(event.kind)}
                </span>
            )
        },
        {
            // Its own column again, rendered with the same SolidStatusPill the
            // Work Queue uses for manufacturer status.
            key: 'status',
            label: 'Status',
            width: '12%',
            cellClassName: EVENTS_TD_PADDING,
            headerClassName: `${EVENTS_TH_PADDING} whitespace-nowrap`,
            render: (event) => {
                const status = eventStatus(event);
                return (
                    <SolidStatusPill
                        value={status}
                        label={EVENT_STATUS_LABEL[status]}
                        toneClassName={EVENT_STATUS_TONE[status]}
                    />
                );
            }
        },
        {
            key: 'startsAt',
            label: 'Event date',
            width: '14%',
            sortable: true,
            sortKey: 'startsAt',
            cellClassName: `${EVENTS_TD_PADDING} text-sm text-gray-600 whitespace-nowrap`,
            headerClassName: `${EVENTS_TH_PADDING} whitespace-nowrap`,
            // An announcement has no meeting time - its `startsAt` is just the
            // end of the banner window, already shown by the next column.
            render: (event) =>
                hasSignups(event) ? (
                    formatShortDate(event.startsAt)
                ) : (
                    <span className="text-gray-300">&mdash;</span>
                )
        },
        {
            // Show from / show until were two columns for one fact: the window
            // the banner is live. Merged the way Location merges city + state.
            key: 'showFrom',
            label: 'Banner window',
            width: '18%',
            sortable: true,
            sortKey: 'showFrom',
            cellClassName: `${EVENTS_TD_PADDING} text-sm text-gray-500 whitespace-nowrap`,
            headerClassName: `${EVENTS_TH_PADDING} whitespace-nowrap`,
            render: (event) =>
                `${formatShortDate(event.showFrom)} → ${formatShortDate(event.showUntil)}`
        },
        {
            // Spelled out rather than shown as a count alone: "3" next to a
            // Sign-ups column of counts would read as a second tally.
            key: 'audience',
            label: 'Audience',
            width: '12%',
            cellClassName: `${EVENTS_TD_PADDING} text-[12px] whitespace-nowrap`,
            headerClassName: `${EVENTS_TH_PADDING} whitespace-nowrap`,
            render: (event) => {
                const count = event.practices?.length ?? 0;
                // Empty means every practice - the overwhelmingly common case,
                // so it is muted and the narrowed one carries the emphasis.
                if (count === 0) return <span className="text-gray-400">Every practice</span>;
                return (
                    <span className="font-medium text-gray-700">
                        {count === 1 ? '1 practice' : `${count} practices`}
                    </span>
                );
            }
        },
        {
            key: 'signups',
            label: 'Sign-ups',
            width: '8%',
            cellClassName: `${EVENTS_TD_PADDING} tabular-nums font-medium text-gray-700 whitespace-nowrap`,
            headerClassName: `${EVENTS_TH_PADDING} whitespace-nowrap`,
            // A count, not a control. 0 is a genuine count, styled muted; an
            // announcement takes none at all, hence a dash rather than a 0.
            render: (event) =>
                hasSignups(event) ? (
                    event.signupCount > 0 ? (
                        event.signupCount
                    ) : (
                        <span className="text-gray-300">0</span>
                    )
                ) : (
                    <span className="text-gray-300">&mdash;</span>
                )
        },
        {
            key: 'menu',
            label: '',
            width: '6%',
            // No overflow-hidden here (unlike EVENTS_TD_PADDING): it would clip
            // the RowMenu dropdown, absolutely positioned inside this cell.
            cellClassName: 'px-3 last:pr-4',
            headerClassName: EVENTS_TH_PADDING,
            stopClickPropagation: true,
            render: (event) => (
                <div className="flex justify-end">
                    <RowMenu
                        items={menuItemsFor(event, {
                            onEdit,
                            onDuplicate,
                            onToggleEnabled,
                            onViewSignups,
                            onDelete,
                            isToggling: togglingId === event.id,
                            canManage,
                            canDelete,
                            canViewSignups
                        })}
                    />
                </div>
            )
        }
    ];
}
